// PII approval modal — review, edit, highlight, and manually redact PII
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Shield, ShieldAlert, ShieldCheck, Eye, EyeOff, Plus, Trash2, Pencil, AlertTriangle, RefreshCw } from 'lucide-react'
import { PII_TYPE_LABELS, PII_RISK_LEVEL } from '@/types/pii'
import { PIITextHighlighter } from './PIITextHighlighter'
import type { PIIItem, PIIDecision, PIIType } from '@/types/pii'

interface PIIApprovalModalProps {
  items: PIIItem[]
  redactedText: string
  scanError?: string | null
  isRetrying?: boolean
  onApprove: (decisions: PIIDecision[]) => void
  onCancel: () => void
  onRetry?: () => void
}

const RISK_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-amber-100 text-amber-800 border-amber-300',
  medium: 'bg-blue-100 text-blue-800 border-blue-300',
}

const PII_TYPES_FOR_MANUAL: { value: PIIType; label: string }[] = [
  { value: 'name', label: 'Name' }, { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' }, { value: 'address', label: 'Address' },
  { value: 'ssn', label: 'SSN/ID' }, { value: 'bank_account', label: 'Bank Account' },
  { value: 'other', label: 'Other' },
]

export function PIIApprovalModal({
  items, redactedText, scanError, isRetrying, onApprove, onCancel, onRetry,
}: PIIApprovalModalProps): JSX.Element {
  const [allItems, setAllItems] = useState<PIIItem[]>(items)
  const [decisions, setDecisions] = useState<Record<number, 'redact' | 'keep'>>(() => {
    const initial: Record<number, 'redact' | 'keep'> = {}
    items.forEach((_, i) => { initial[i] = 'redact' })
    return initial
  })
  const [showPreview, setShowPreview] = useState(false)
  const [manualText, setManualText] = useState('')
  const [manualType, setManualType] = useState<PIIType>('name')
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [editType, setEditType] = useState<PIIType>('name')
  const [editOriginal, setEditOriginal] = useState('')
  const [editRedacted, setEditRedacted] = useState('')

  const hasItems = allItems.length > 0
  const redactCount = Object.values(decisions).filter((d) => d === 'redact').length

  const handleToggle = (idx: number) => {
    setDecisions((prev) => ({ ...prev, [idx]: prev[idx] === 'redact' ? 'keep' : 'redact' }))
  }

  const handleStartEdit = (idx: number) => {
    const item = allItems[idx]
    setEditingIdx(idx)
    setEditType(item.type)
    setEditOriginal(item.original)
    setEditRedacted(item.redacted)
  }

  const handleSaveEdit = () => {
    if (editingIdx === null) return
    setAllItems((prev) => prev.map((item, i) =>
      i === editingIdx ? { ...item, type: editType, original: editOriginal, redacted: editRedacted } : item,
    ))
    setEditingIdx(null)
  }

  const handleHighlightAdd = (original: string, type: PIIType) => {
    const token = `[${type.toUpperCase()}]`
    const newItem: PIIItem = {
      type, original, redacted: token, confidence: 1.0, context: 'Highlighted by user',
    }
    const newIdx = allItems.length
    setAllItems((prev) => [...prev, newItem])
    setDecisions((prev) => ({ ...prev, [newIdx]: 'redact' }))
    setShowPreview(false)
  }

  const handleAddManual = () => {
    if (!manualText.trim()) return
    const token = `[${manualType.toUpperCase()}]`
    const newItem: PIIItem = {
      type: manualType, original: manualText.trim(),
      redacted: token, confidence: 1.0, context: 'Manually flagged by user',
    }
    const newIdx = allItems.length
    setAllItems((prev) => [...prev, newItem])
    setDecisions((prev) => ({ ...prev, [newIdx]: 'redact' }))
    setManualText('')
    setShowManualAdd(false)
  }

  const handleRemoveManual = (idx: number) => {
    setAllItems((prev) => prev.filter((_, i) => i !== idx))
    setDecisions((prev) => {
      const updated: Record<number, 'redact' | 'keep'> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const key = parseInt(k)
        if (key < idx) updated[key] = v
        else if (key > idx) updated[key - 1] = v
      })
      return updated
    })
  }

  const handleApprove = () => {
    const result: PIIDecision[] = allItems.map((item, i) => ({
      decision: decisions[i] || 'redact', item,
    }))
    onApprove(result)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="border-b bg-kaycoo-teal-dark p-4 text-white">
          <div className="flex items-center gap-2">
            {hasItems ? <ShieldAlert className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
            <h2 className="text-lg font-semibold">
              {hasItems ? 'PII Detected — Review Required' : 'Document Review — No PII Detected'}
            </h2>
          </div>
          <p className="mt-1 text-sm text-kaycoo-teal-pale">
            {hasItems
              ? `${allItems.length} personal data item${allItems.length !== 1 ? 's' : ''} found. Review before proceeding.`
              : 'No personal data was automatically detected. You can manually flag items or confirm to proceed.'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button onClick={() => setShowPreview(false)}
            className={`flex-1 px-4 py-2 text-sm font-medium ${!showPreview ? 'border-b-2 border-kaycoo-teal-dark text-kaycoo-teal-dark' : 'text-kaycoo-charcoal-2'}`}>
            Review {allItems.length > 0 ? `(${allItems.length})` : ''}
          </button>
          <button onClick={() => setShowPreview(true)}
            className={`flex-1 px-4 py-2 text-sm font-medium ${showPreview ? 'border-b-2 border-kaycoo-teal-dark text-kaycoo-teal-dark' : 'text-kaycoo-charcoal-2'}`}>
            Preview Document
          </button>
        </div>

        {/* Error Alert */}
        {scanError && (
          <Alert className="m-4 border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="flex items-center justify-between text-amber-800">
              <span className="text-sm">
                <strong>PII scan incomplete:</strong> {scanError}. You can retry the scan or proceed manually.
              </span>
              {onRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRetry}
                  disabled={isRetrying}
                  className="ml-4 shrink-0 border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  <RefreshCw className={`mr-1 h-3 w-3 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Retrying...' : 'Retry Scan'}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Content */}
        <div className="max-h-[50vh] overflow-y-auto p-4">
          {showPreview ? (
            <PIITextHighlighter text={redactedText} onAddPII={handleHighlightAdd} />
          ) : (
            <div className="space-y-3">
              {allItems.map((item, idx) => {
                const risk = PII_RISK_LEVEL[item.type as PIIType] || 'medium'
                const isRedacting = decisions[idx] === 'redact'
                const isManual = idx >= items.length
                return (
                  <div key={idx} className="rounded-lg border p-3">
                    {editingIdx === idx ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <select value={editType} onChange={(e) => setEditType(e.target.value as PIIType)}
                            className="rounded border px-2 py-1 text-xs">
                            {PII_TYPES_FOR_MANUAL.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <Input value={editOriginal} onChange={(e) => setEditOriginal(e.target.value)}
                            placeholder="Original text" className="text-xs" />
                        </div>
                        <div className="flex gap-2">
                          <Input value={editRedacted} onChange={(e) => setEditRedacted(e.target.value)}
                            placeholder="Replacement (e.g. [NAME], ***)" className="text-xs" />
                          <Button size="sm" onClick={handleSaveEdit} className="shrink-0 bg-kaycoo-teal-dark text-white text-xs">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingIdx(null)} className="text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs ${RISK_COLORS[risk]}`}>{risk}</Badge>
                            <span className="text-xs font-medium text-kaycoo-charcoal">
                              {PII_TYPE_LABELS[item.type as PIIType] || item.type}
                            </span>
                            {isManual && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700">Manual</Badge>}
                            {!isManual && (
                              <span className="text-[10px] text-kaycoo-charcoal-2">
                                {(item.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <code className={`rounded px-1.5 py-0.5 text-xs ${isRedacting ? 'bg-red-50 text-red-700 line-through' : 'bg-gray-50 text-kaycoo-charcoal'}`}>
                              {item.original}
                            </code>
                            {isRedacting && (
                              <>
                                <span className="text-xs text-kaycoo-charcoal-2">→</span>
                                <code className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700">{item.redacted}</code>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => handleStartEdit(idx)}
                            className="text-xs text-kaycoo-charcoal-2 hover:text-kaycoo-teal-dark">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggle(idx)}
                            className={`gap-1 text-xs ${isRedacting ? 'text-red-600' : 'text-emerald-600'}`}>
                            {isRedacting ? <><EyeOff className="h-3 w-3" /> Redact</> : <><Eye className="h-3 w-3" /> Keep</>}
                          </Button>
                          {isManual && (
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveManual(idx)}
                              className="text-xs text-gray-400 hover:text-red-500">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Manual add section */}
              {showManualAdd ? (
                <div className="rounded-lg border-2 border-dashed border-kaycoo-teal-light p-3 space-y-2">
                  <p className="text-xs font-medium text-kaycoo-charcoal-2">Manually flag text as PII</p>
                  <div className="flex gap-2">
                    <select value={manualType} onChange={(e) => setManualType(e.target.value as PIIType)}
                      className="rounded border px-2 py-1.5 text-xs">
                      {PII_TYPES_FOR_MANUAL.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <Input value={manualText} onChange={(e) => setManualText(e.target.value)}
                      placeholder="Type or paste text to redact..." className="text-xs" />
                    <Button size="sm" onClick={handleAddManual} disabled={!manualText.trim()}
                      className="shrink-0 bg-kaycoo-teal-dark text-white">Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowManualAdd(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setShowManualAdd(true)}
                  className="w-full gap-1.5 border-dashed text-kaycoo-charcoal-2">
                  <Plus className="h-3.5 w-3.5" /> Manually Flag PII
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t bg-gray-50 p-4">
          <p className="text-xs text-kaycoo-charcoal-2">
            <Shield className="mr-1 inline h-3 w-3" />
            {redactCount > 0 ? `${redactCount} item${redactCount !== 1 ? 's' : ''} will be redacted` : 'No redactions — document will be stored as-is'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel}>Cancel Upload</Button>
            <Button size="sm" onClick={handleApprove}
              className="gap-1.5 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
              <ShieldCheck className="h-3.5 w-3.5" />
              {hasItems ? 'Approve & Continue' : 'Confirm & Upload'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
